import { useEffect, useRef } from 'react'

const STORAGE_PREFIX = 'opus:scroll:'

/**
 * Persist and restore scroll position for window or a scrollable container.
 * Saves on unmount / pagehide; restores once when `ready` is true.
 *
 * @param {string} storageKey
 * @param {{
 *   containerRef?: React.RefObject<HTMLElement | null>,
 *   ready?: boolean,
 * }} [options]
 */
export function useScrollRestoration(storageKey, { containerRef = null, ready = true } = {}) {
  const restoredRef = useRef(false)
  const key = `${STORAGE_PREFIX}${storageKey}`

  useEffect(() => {
    restoredRef.current = false
  }, [key])

  useEffect(() => {
    const readEl = () => (containerRef?.current ? containerRef.current : null)

    const save = () => {
      try {
        const el = readEl()
        const y = el ? el.scrollTop : window.scrollY || window.pageYOffset || 0
        sessionStorage.setItem(key, String(Math.round(y)))
      } catch {
        // ignore quota / private mode
      }
    }

    const onPageHide = () => save()
    window.addEventListener('pagehide', onPageHide)
    return () => {
      save()
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [key, containerRef])

  useEffect(() => {
    if (!ready || restoredRef.current) return undefined

    let raw
    try {
      raw = sessionStorage.getItem(key)
    } catch {
      return undefined
    }
    if (raw == null || raw === '') {
      restoredRef.current = true
      return undefined
    }
    const y = Number(raw)
    if (!Number.isFinite(y) || y < 0) {
      restoredRef.current = true
      return undefined
    }

    let cancelled = false
    const apply = () => {
      if (cancelled) return
      const el = containerRef?.current
      if (el) el.scrollTop = y
      else window.scrollTo(0, y)
      restoredRef.current = true
    }

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(apply)
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(id)
    }
  }, [key, ready, containerRef])
}

/**
 * @param {string} storageKey
 */
export function clearScrollRestoration(storageKey) {
  try {
    sessionStorage.removeItem(`${STORAGE_PREFIX}${storageKey}`)
  } catch {
    // ignore
  }
}
